import { useEffect, useState } from "react";
import { getAuth, updateEmail, updatePassword } from "firebase/auth";
import { ref, onValue, update } from "firebase/database";
import { ref as sRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { database, storage } from "./firebase";

const AccountSettings = () => {
  const auth = getAuth();
  const currentUser = auth.currentUser;

  const [profile, setProfile] = useState({
    fullname: "",
    email: "",
    phone: "",
    role: "",
    profilePicture: "",
  });

  const [password, setPassword] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [uploading, setUploading] = useState(false);

  /* ================= LOAD USER ================= */
  useEffect(() => {
    if (!currentUser) return;

    const userRef = ref(database, `/helpdesk/users/${currentUser.uid}`);
    const unsub = onValue(userRef, (snap) => {
      if (snap.exists()) {
        setProfile((prev) => ({
          ...prev,
          ...snap.val(),
          email: snap.val().email || currentUser.email,
        }));
      }
    });

    return () => unsub();
  }, [currentUser]);

  /* ================= IMAGE PREVIEW ================= */
  const handleImagePreview = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setPreviewImage(URL.createObjectURL(file));
    uploadImage(file);
  };

  /* ================= IMAGE UPLOAD ================= */
  const uploadImage = async (file) => {
    if (!currentUser) return;

    try {
      setUploading(true);

      const imageRef = sRef(storage, `profile-images/${currentUser.uid}`);

      await uploadBytes(imageRef, file);
      const imageUrl = await getDownloadURL(imageRef);

      await update(ref(database, `/helpdesk/users/${currentUser.uid}`), {
        profilePicture: imageUrl,
      });

      setProfile((p) => ({ ...p, profilePicture: imageUrl }));
    } catch (err) {
      alert(err.message);
    } finally {
      setUploading(false);
    }
  };

  /* ================= UPDATE PROFILE ================= */
  const updateProfile = async (e) => {
    e.preventDefault();

    try {
      if (profile.email !== currentUser.email) {
        await updateEmail(currentUser, profile.email);
      }

      if (password) {
        await updatePassword(currentUser, password);
      }

      await update(ref(database, `/helpdesk/users/${currentUser.uid}`), {
        fullname: profile.fullname,
        email: profile.email,
        phone: profile.phone,
      });

      alert("Account updated successfully");
      setPassword("");
    } catch (err) {
      alert(err.message);
    }
  };

  /* ================= RENDER ================= */
  return (
    <div className="container mt-4">
      {/* HEADER */}
      <div className="card shadow-sm mb-4">
        <div className="card-body d-flex align-items-center">
          <div className="position-relative mr-3">
            <img
              src={
                previewImage ||
                profile.profilePicture ||
                `https://api.dicebear.com/7.x/avataaars/svg?seed=${currentUser?.uid}`
              }
              alt="Profile"
              className="rounded-circle border"
              width={72}
              height={72}
              style={{ objectFit: "cover" }}
            />

            <label
              className="btn btn-sm btn-light position-absolute"
              style={{ bottom: -5, right: -5 }}
            >
              <i className="fas fa-camera"></i>
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImagePreview}
              />
            </label>
          </div>

          <div>
            <h5 className="mb-0">{profile.fullname || "User"}</h5>
            <small className="text-gray-300 text-capitalize">
              {profile.role}
            </small>
          </div>
        </div>
      </div>

      {/* FORM */}
      <div className="card shadow-sm">
        <div className="card-body">
          <form onSubmit={updateProfile}>
            <div className="form-group">
              <label>Full Name</label>
              <input
                className="form-control"
                value={profile.fullname}
                onChange={(e) =>
                  setProfile({ ...profile, fullname: e.target.value })
                }
                required
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                className="form-control"
                type="email"
                value={profile.email}
                onChange={(e) =>
                  setProfile({ ...profile, email: e.target.value })
                }
                required
              />
            </div>

            <div className="form-group">
              <label>Phone</label>
              <input
                className="form-control"
                value={profile.phone || ""}
                onChange={(e) =>
                  setProfile({ ...profile, phone: e.target.value })
                }
              />
            </div>

            <hr />

            <div className="form-group">
              <label>New Password</label>
              <input
                className="form-control"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Leave blank to keep current password"
              />
            </div>

            <button
              className="btn btn-primary btn-block"
              type="submit"
              disabled={uploading}
            >
              <i className="fas fa-save mr-2"></i>
              Save Changes
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
